import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { uniq } from "lodash";
import networkApi from "../api";
import { USEQUERY_DISABLE_REFETCH_OPTIONS } from "../consts";
import { chunkData } from "../util";
import { DomainReport, IPReport, UrlReport } from "../@types";
import { NetworkRepEntry } from "../@types/Url";

const QUERY_KEY_ID = "bulkNetworkCategories";
const CATEGORIES_CHUNK_SIZE = 100;

type AnyReport = Partial<UrlReport> & Partial<DomainReport> & Partial<IPReport>;

type ReportsResponse = { reports?: AnyReport[] };

const getReportLocation = (report: AnyReport) =>
  report.requested_url ?? report.requested_domain ?? report.requested_ip;

const getReportCategories = (report: AnyReport) =>
  uniq(
    [
      ...(report.categories ?? []),
      ...(report.third_party_reputations?.sources ?? []).flatMap(
        source => source.categories ?? []
      )
    ].filter(Boolean)
  );

const getRequestLocation = (entry: NetworkRepEntry) =>
  entry.type === "url"
    ? entry.normalized_url ?? entry.requested_network_location
    : entry.requested_network_location;

const bulkReportRequest = (
  type: NetworkRepEntry["type"],
  chunk: string[],
  signal: AbortSignal
) => {
  if (type === "url") return networkApi.bulkUrlReport(chunk, signal);
  if (type === "domain") return networkApi.bulkDomainReport(chunk, signal);
  return networkApi.bulkIPReport(chunk, signal);
};

const useBulkNetworkCategories = (entries: NetworkRepEntry[]) => {
  const chunksWithType = useMemo(() => {
    const locationsByType: Record<NetworkRepEntry["type"], string[]> = {
      url: [],
      domain: [],
      ip: []
    };
    entries.forEach(entry => {
      const location = getRequestLocation(entry);
      if (entry.type && location) locationsByType[entry.type].push(location);
    });

    return (
      Object.entries(locationsByType) as Array<
        [NetworkRepEntry["type"], string[]]
      >
    ).flatMap(([type, locations]) =>
      chunkData(uniq(locations), CATEGORIES_CHUNK_SIZE).map(
        chunk => [type, chunk] as const
      )
    );
  }, [entries]);

  return useQueries({
    queries: chunksWithType.map(([type, chunk]) => ({
      queryKey: [QUERY_KEY_ID, type, chunk],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        bulkReportRequest(type, chunk, signal).then(
          resp => (resp.data ?? {}) as ReportsResponse
        ),
      retry: false,
      enabled: Boolean(chunk.length),
      ...USEQUERY_DISABLE_REFETCH_OPTIONS
    })),
    combine: results => {
      const map = new Map<string, string[]>();
      results.forEach(result => {
        (result.data?.reports ?? []).forEach(report => {
          const location = getReportLocation(report);
          const categories = getReportCategories(report);
          if (location && categories.length) map.set(location, categories);
        });
      });
      return {
        categoriesByLocation: map,
        isLoading: results.some(result => result.isLoading)
      };
    }
  });
};

export const getEntryCategories = (
  categoriesByLocation: Map<string, string[]>,
  entry: {
    requested_network_location?: string;
    normalized_url?: string;
  }
) =>
  (entry.requested_network_location &&
    categoriesByLocation.get(entry.requested_network_location)) ||
  (entry.normalized_url && categoriesByLocation.get(entry.normalized_url)) ||
  undefined;

export const withNetworkCategories = <
  T extends {
    requested_network_location?: string;
    normalized_url?: string;
    categories?: string[];
  }
>(
  items: T[],
  categoriesByLocation: Map<string, string[]>,
  isLoading = false
): Array<T & { categoriesLoading?: boolean }> =>
  categoriesByLocation.size || isLoading
    ? items.map(item => {
        const categories = getEntryCategories(categoriesByLocation, item);
        if (categories) {
          return {
            ...item,
            categories: uniq([...(item.categories ?? []), ...categories])
          };
        }
        return isLoading && !item.categories?.length
          ? { ...item, categoriesLoading: true }
          : item;
      })
    : items;

export default useBulkNetworkCategories;
